// Topbar global search (Admin + Seller). One trigger in the topbar — the wide
// search field on desktop, an icon button on phones — and Ctrl/Cmd+K anywhere
// in the shell open a command palette that searches orders, products, stores
// and (admin only) suppliers, grouped, keyboard-navigable, click to open.
//
// The palette never filters locally (`shouldFilter={false}`): every visible row
// came back from GET /{shell}/search, so the seller lens can only ever show the
// seller's own rows — the ownership rule lives on the backend, not here.
//
// The palette body is lazy: the topbar renders on every authenticated screen,
// and only the trigger needs to exist until someone actually searches.
import { lazy, Suspense, useEffect, useState } from 'react'
import { Dialog } from '../ui/dialog.lib'
import { PexIcon } from '../icons/PexIcon'
import { useT } from '../../i18n'
import { globalSearchI18n } from '../../i18n/shared/global-search'
import type { GlobalSearchShell } from '../../api/global-search'

type PaletteModule = typeof import('./GlobalSearchPalette')
// Once loaded, the palette renders directly (no Suspense round-trip), so the
// input is mounted — and focused — in the same frame the dialog opens.
let loadedPalette: PaletteModule | null = null
const loadPalette = () =>
  import('./GlobalSearchPalette').then((module) => {
    loadedPalette = module
    return module
  })
const LazyPalette = lazy(loadPalette)

// Warm the palette shortly after the shell renders (and on hover/focus of the
// trigger), so Ctrl/Cmd+K followed by immediate typing never loses the first
// keystrokes to a still-loading chunk.
const PRELOAD_DELAY_MS = 1500

function isMac(): boolean {
  return typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
}

export function GlobalSearch({ shell }: { shell: GlobalSearchShell }) {
  const t = useT(globalSearchI18n)
  const [open, setOpen] = useState(false)
  const shortcut = isMac() ? '⌘K' : 'Ctrl K'

  // Ctrl/Cmd+K toggles the palette from anywhere in the shell. Browsers bind
  // the same chord to their own search bar, so the default is suppressed.
  useEffect(() => {
    const id = window.setTimeout(() => void loadPalette(), PRELOAD_DELAY_MS)
    return () => window.clearTimeout(id)
  }, [])

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setOpen((value) => !value)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  const triggerText = shell === 'admin' ? t.triggerAdmin : t.triggerSeller

  return (
    <>
      <button
        type="button"
        data-global-search-trigger
        onClick={() => setOpen(true)}
        onPointerEnter={() => void loadPalette()}
        onFocus={() => void loadPalette()}
        aria-label={t.openLabel}
        aria-keyshortcuts="Control+K Meta+K"
        className="hidden h-[var(--topbar-placeholder-height)] w-full max-w-[var(--topbar-placeholder-width)] items-center gap-2 rounded-[var(--radius-control)] border border-[var(--topbar-placeholder-border)] bg-[var(--topbar-placeholder-bg)] px-3 text-left text-xs text-[var(--topbar-muted)] transition-colors hover:border-[var(--topbar-muted)] focus-visible:outline-none focus-visible:shadow-[var(--shadow-focus)] md:flex"
      >
        <PexIcon name="search" size="sm" tone="topbar" className="shrink-0 opacity-80" />
        <span className="min-w-0 flex-1 truncate">{triggerText}</span>
        <kbd className="shrink-0 rounded border border-[var(--topbar-placeholder-border)] px-1.5 py-0.5 font-sans text-[10px] leading-none text-[var(--topbar-muted)]">
          {shortcut}
        </kbd>
      </button>
      <button
        type="button"
        data-global-search-trigger-mobile
        onClick={() => setOpen(true)}
        aria-label={t.openLabel}
        className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-[var(--radius-control)] p-2 text-[var(--topbar-fg)] hover:bg-[var(--topbar-placeholder-bg)] focus-visible:outline-none focus-visible:shadow-[var(--shadow-focus)] md:hidden"
      >
        <PexIcon name="search" size="md" tone="topbar" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        {open &&
          (loadedPalette ? (
            <loadedPalette.default shell={shell} onClose={() => setOpen(false)} />
          ) : (
            <Suspense fallback={null}>
              <LazyPalette shell={shell} onClose={() => setOpen(false)} />
            </Suspense>
          ))}
      </Dialog>
    </>
  )
}

